import {
  PromptConfirmItemType,
  PromptListType,
} from '@op/components/GPT/types';
import { AnalysisSummaryTypeTab } from '../../types';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import React, { useEffect, useMemo, useState } from 'react';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM } from '@op/utils/format';
import { CONTACT_EMAIL } from '@op/constants';
import { Button, Col, Icon, Row } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import CardCommon from '@op/components/CardCommon';
import GPT from '@op/components/GPT';
export type TGptErrorType = { code: number; message: string };
export default function GPTSummaryContent({
  isPreviews,
  taskId,
  promptInfo,
  value,
  filter,
  saveTipsElement,
  promptList,
  setApiLoading,
  setPromptInfo,
  keywordGptError,
  keywordGptLoading,
  updateGptParams,
  stopUpdateGpt = false,
}: {
  saveTipsElement: any;
  isPreviews: boolean;
  promptList: PromptListType;
  setApiLoading?: (flag: string) => void;
  promptInfo: any;
  setPromptInfo: (value: any, params?: any) => void;
  taskId?: string;
  filter: any;
  value: AnalysisSummaryTypeTab;
  keywordGptError?: TGptErrorType;
  keywordGptLoading: boolean;
  updateGptParams?: any;
  stopUpdateGpt?: boolean;
}) {
  const { formatMessage } = useLanguage();
  const [dataInfo, setDataInfo] = useState<{ start_time: string; end_time: string } | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { summaryRef, getSummary, isFirst, toggleSummaryPrompt, showChildren } =
    useGeySummary({ isPreviews });

  useEffect(() => {
    if (keywordGptLoading) return;
    let timer = null;
    if (!taskId) {
      showChildren();
      setApiLoading?.('gpt');
    } else {
      timer = setTimeout(() => {
        getSummary({ ...promptInfo, taskId });
      }, 500);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [taskId, keywordGptLoading]);

  // 当 keywordGptError 变化时（如轮询返回新数据），重置重新生成标记
  useEffect(() => {
    setIsRegenerating(false);
  }, [keywordGptError]);

  const { isEmpty, emptyDesc, hasEmptyImg } = useMemo(() => {
    // 重新生成期间不显示空状态，让 PageCard 展示自身的 loading 状态
    if (isRegenerating) {
      return { isEmpty: undefined };
    }

    const { code } = keywordGptError || {};

    if (!code) {
      return { isEmpty: undefined };
    }

    function getCodeTips(code: number) {
      if (code === 30018) {
        return formatMessage(
          {
            id: 'Please try again tomorrow or contact {name} for more usage.',
          },
          {
            name: <i style={{ color: '#326FF0' }}>{CONTACT_EMAIL}</i>,
          },
        );
      }

      return formatMessage({
        id: 'Summary API is currently unstable, please try again',
      });
    }

    return {
      isEmpty: true,
      emptyDesc:
        code !== 40001 ? (
          <ul className="dbo-no-data-ul">
            <li>
              <span className="dbo-no-data-ul-icon">
                <Icon type="error" />
              </span>
              {getCodeTips(code)}
            </li>
            <li>
              <Button
                type="primary"
                className="dbo-no-data-ul-btn"
                onClick={() => toggleSummaryPrompt?.()}
              >
                <SvgIcon type="refresh" />
                {formatMessage({ id: 'Regenerate AI Summary' })}
              </Button>
            </li>
          </ul>
        ) : undefined,
      hasEmptyImg: code === 40001,
    };
  }, [keywordGptError, isRegenerating]);
  
  return (
    <Row
      className="dbo-analysis-card-container"
      style={{ display: value === 'total' ? 'block' : 'none' }}
    >
      <Col span={24}>
        <CardCommon
          loadingHeight={220}
          loading={keywordGptLoading}
          loadingText={formatMessage({ id: 'Preparing the summary task' })}
          className="dbo-aigc-card-wrapper"
        >
          <GPT.PageCard
            className={'gpt-summary'}
            loadingHeight={560}
            filter={filter}
            title={
              dataInfo
                ? (
                  <>
                    {formatMessage({ id: 'AI Summary' })}
                    <span style={{ marginLeft: 8, fontSize: 12, color: '#999', fontWeight: 'normal' }}>
                      {DBO_TM.timeTitle({ ...dataInfo, date_type: 'daily', link_text: ' ~ ' })}
                    </span>
                  </>
                )
                : undefined
            }
            contentCard
            hasStopBtn
            alwaysShowTitle
            ref={summaryRef}
            loadingElement={saveTipsElement}
            promptList={promptList}
            needBasicTaskId
            hasResultOutput={false}
            businessType="keyword_analysis"
            backGPTText={(value: any) => {
              if (value?.task_status === 'success') {
                setApiLoading?.('gpt');
              }
              if (value?.data_info) {
                setDataInfo(value.data_info);
              }
            }}
            linkFilterChange={false}
            promptValue={promptInfo}
            promptConfirm={(data: PromptConfirmItemType) => {
              if (stopUpdateGpt) return;
              setIsRegenerating(true);
              const params = updateGptParams
                ? { ...updateGptParams, summary_task_id: data.taskId }
                : null;
              setPromptInfo(data, params);
            }}
            empty={isEmpty}
            emptyDescText={emptyDesc}
            hasEmptyImg={hasEmptyImg}
            hasEmptyTitle={hasEmptyImg}
          >
            {(toggleVisible: () => void, { loading, total }: any) => (
              <GPT.Button
                disabled={loading && total > 0}
                onClick={() => toggleVisible()}
              ></GPT.Button>
            )}
          </GPT.PageCard>
        </CardCommon>
      </Col>
    </Row>
  );
}
